import { useEffect } from 'react';

import { progressPercent } from './progressPercent';

/* Once per session, not once per ring — a register draws one of these on every row. */
let warnedAboutSmallFraction = false;

/**
 * How far through a countable thing something is — a RING over its own figure.
 *
 * **Product call, 01 Oct 2026, from an image the reader attached**: a ring rather than a filled
 * wedge, a grey full-circle track behind a blue arc with ROUNDED CAPS, starting at twelve
 * o'clock and running clockwise, with the figure centred INSIDE the hole in the same blue. It
 * was a filled disc with the figure captioned beneath it; every part of that is the image's.
 *
 * **The figure moved INSIDE, which is what sets every size here.** A caption below could be any
 * width the stack allowed; a figure inside has to fit the hole, so the ring's diameter is now
 * derived from the longest value it will ever hold rather than chosen. See `size`.
 *
 * **It renders the FIGURE as well as the ring, and that pairing is the whole component.** A ring
 * on its own is a fact only a sighted reader gets, which is the same objection the calendar's
 * marker dot answers by putting its meaning in the cell's accessible name. Owning both halves
 * here is what makes it impossible to use silently — the shape `<HelpTip>` takes with its
 * `sr-only` twin.
 *
 * **WHAT it counts is the CALLER's decision, and `label` is what makes the ring honest about
 * it.** This component is given a `value`, a `total` and the word for them, so a caller may
 * count lines, units, or anything else it can state — the `sr-only` twin reads
 * `{value} of {total} {label}`, which is the only place the unit is named.
 *
 * **It used to say "it counts THINGS, never a quantity summed across units"**, on the ground
 * that a ring over "boxes plus laptops" is a figure no report can add together. That is a real
 * cost and it is now the CALLER's to weigh: the purchase request register counts quantity on a
 * product call of 05 Oct 2026, so the rule moved out of this component rather than being
 * broken inside it.
 */
export function ProgressPie({
  value,
  total,
  label,
  format = 'fraction',
  size = 'md',
  className = '',
}: {
  /** How many are done. Clamped into `0..total`, so a stale figure cannot overdraw the arc. */
  value: number;
  total: number;
  /**
   * What is being counted, for the screen reader — "lines ordered". The visible figure is bare
   * digits, which says nothing on its own when it is read out of its column.
   */
  label: string;
  /**
   * What the figure INSIDE the ring reads — the FRACTION by default, or a PERCENTAGE.
   *
   * Product call, 01 Oct 2026: *it must display the % progress of the pr request.* A register
   * row is scanned down a column, and `60%` is one value to compare where `3/5` is two — but
   * the wizard's source card reads *"N lines left to order"* one line below its own ring, and
   * there the fraction is what that sentence is about. **So it is a prop rather than a change**:
   * the default is what every existing caller already draws.
   *
   * <b>The SR text is the fraction either way.</b> A percentage read out of its column says how
   * far through something is and not how much of it there is, and the counts are the half a
   * reader acting on the row needs.
   *
   * **The FORMAT is what bounds the ring.** `100%` is four characters and `12/45` is five, so a
   * `percent` ring can be smaller than a `fraction` one and still hold its own figure — which is
   * exactly why `sm` is percent-sized and `md` is fraction-sized. A caller pairing `sm` with
   * `fraction` is asking a 40px ring to hold five characters, and it will overflow.
   */
  format?: 'fraction' | 'percent';
  /**
   * The ring's own size — `md` on a card, `sm` in a LIST ROW.
   *
   * **Both are sized to what has to fit INSIDE them**, which is the constraint the old captioned
   * disc did not have. The hole is 72.5% of the diameter, and Geist Mono advances about 0.6em
   * per character:
   *
   * ```
   * sm  40px ring → 29px hole   `100%` at  9px = 21.6px → 74% of it   the list row
   * md  56px ring → 41px hole   `12/45` at 11px = 33px   → 81% of it  the wizard card
   * ```
   *
   * **THE FIGURE IS PROPORTIONALLY LARGER THAN THE REFERENCE IMAGE'S, and that is the one thing
   * given up.** There `70%` fills about half its hole; here it fills three quarters. The image
   * is a ~400px ring where the figure can be a twelfth of the diameter and still be read, and a
   * list row caps this at 40px — so matching the proportion exactly would put the figure at
   * about 6px, which is not a number anybody reads. The RING's proportions are the image's; the
   * figure is as large as the hole allows at this scale.
   *
   * **`sm` is ALSO bounded from above by the list row's own content block**, measured at 41px —
   * a wedge taller than that makes every row carrying one stand proud of the rows between it,
   * and the eye loses the line it scans down. 40px is that block with a pixel to spare, and the
   * figure moving inside is what bought the room: the old stack was 24 + 2 + 11 = 37px for a
   * 24px disc, where this is 40px of ring and nothing else.
   *
   * **Changing either step means re-measuring against a row AND against the longest figure**,
   * not adjusting until it looks right.
   */
  size?: 'sm' | 'md';
  className?: string;
}) {
  /*
    NOTHING TO BE A FRACTION OF is not a progress of zero — it is a question with no answer, so
    the component draws nothing rather than a full grey ring reading `0/0`. The em-dash rule one
    layer up: an absent value and a zero are different facts.
  */
  /*
    A 40px RING HOLDS A PERCENTAGE AND NOT A FRACTION, and the component says so rather than
    leaving it to be remembered. `100%` is four characters in a 29px hole; `12/45` is five, and
    at this scale the fifth is what pushes the figure past the arc it is supposed to sit inside.
    Caught in development rather than typed away: `size` and `format` are each legitimately
    either value, so the constraint is about the PAIR — which is the `<StackedCell secondary>`
    shape, where what is refused is a combination rather than a prop.
  */
  useEffect(() => {
    if (!import.meta.env.DEV || warnedAboutSmallFraction) return;
    if (size !== 'sm' || format !== 'fraction') return;

    warnedAboutSmallFraction = true;
    console.error(
      '<ProgressPie size="sm"> holds a percentage, not a fraction — a five-character figure ' +
        'overflows a 29px hole. Pass format="percent", or size="md". See CLAUDE.md, ' +
        '"A register with FULFILMENT MONITORING draws a PROGRESS RING before the kebab".',
    );
  }, [size, format]);

  if (total <= 0) return null;

  const done = Math.min(Math.max(value, 0), total);

  /*
    A RING rather than the filled wedge this drew until 01 Oct 2026 — the reader's own image.
    Two circles on one geometry: a grey track all the way round, and the arc over it as a dash
    whose first segment is the progress. `-rotate-90` starts it at twelve o'clock, which is where
    a reader expects a dial to begin.

    THE HOLE IS 72.5% OF THE DIAMETER, read off the reference image rather than chosen: r 17.25
    with a stroke of 5.5 leaves 29 of the 40 viewBox units clear. It was 65% for one build and
    that was a MEASURING failure rather than a judgement — at 65% the hole is 26px and `100%` is
    24px of it, so a fully ordered row rendered as a blue blob with no visible ring at all. The
    stroke and the figure are ONE decision: narrowing the stroke widens the hole and the figure's
    budget with it, and widening it does the reverse.

    The colours are TOKENS through `stroke-*` rather than a hex inside a `conic-gradient`, which
    is the other way to draw this and cannot name a theme colour.
  */
  const RADIUS = 17.25;
  const STROKE = 5.5;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const filled = (done / total) * CIRCUMFERENCE;

  /*
    THE SHARED derivation, never a second copy — a register draws this ring on its list rows and
    the same percentage as a column in its table, and two spellings of one rounding is how those
    two surfaces come to disagree about one record.
  */
  const percent = progressPercent(done, total);

  /*
    Spelled out rather than interpolated — Tailwind scans source for literal class names, so a
    `h-${n}` template is invisible to it and the class is never emitted. The same reason
    `<TextCell>`'s own size map is written out.

    The two steps are the RING and its FIGURE together: they are one measurement, and a ring
    resized without its figure is a hole the value no longer fits.
  */
  const SIZES = {
    sm: { ring: 'h-10 w-10', text: 'text-[9px]' },
    md: { ring: 'h-14 w-14', text: 'text-[11px]' },
  } as const;

  return (
    <span
      className={`relative inline-flex shrink-0 items-center justify-center ${SIZES[size].ring} ${className}`}
    >
      <svg viewBox="0 0 40 40" className="h-full w-full -rotate-90" aria-hidden="true">
        <circle
          cx="20"
          cy="20"
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE}
          className="stroke-slate-200"
        />
        {done > 0 ? (
          <circle
            cx="20"
            cy="20"
            r={RADIUS}
            fill="none"
            strokeWidth={STROKE}
            /*
              ROUNDED CAPS, which is the image's own shape and not a flourish: it is what makes a
              small arc read as a deliberate mark rather than as a clipped sliver. The cost is
              stated rather than hidden — a cap extends the arc by half a stroke at each end, so
              a drawn arc runs very slightly past its own figure. At 100% the two caps meet on a
              closed ring and nothing shows.
            */
            strokeLinecap="round"
            /*
              BLUE, never the brand yellow. Yellow marks chrome the product owns — the active
              rail bar, the step head, a primary action — and this is a fact about a RECORD,
              which that colour never marks. `blue-500` is 3.68:1 on white and clears WCAG
              1.4.11's 3:1 for a meaningful graphic.
            */
            className="stroke-blue-500"
            strokeDasharray={`${filled} ${CIRCUMFERENCE}`}
          />
        ) : null}
      </svg>

      {/*
        THE FIGURE SITS IN THE HOLE, absolutely centred over the ring rather than drawn as SVG
        `<text>`. Two reasons, and both are the product's rather than the renderer's: an HTML span
        takes `font-mono tabular-nums` and the theme's own tones from the same classes every other
        value in the product uses, where SVG text inherits neither reliably.

        It is BLUE, matching the arc — the image's own pairing, and the honest one: the ring and
        the number are one fact drawn twice, so a grey number beside a blue arc would read as two.
      */}
      <span
        aria-hidden="true"
        className={`absolute font-mono tabular-nums leading-none text-blue-500 ${SIZES[size].text}`}
      >
        {format === 'percent' ? `${percent}%` : `${done}/${total}`}
      </span>

      <span className="sr-only">
        {done} of {total} {label}
      </span>
    </span>
  );
}
